import {
    AlertTriangle,
    RefreshCw,
    Shield,
    Unlock,
    Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import { useTheme } from "../../contexts/ThemeContext";
import httpService from "../../services/httpService";

interface AbuseStats {
  totalBlocked: number;
  topBlockedIPs: Array<{ ip: string; count: number }>;
  topBlockedEmails: Array<{ email: string; count: number }>;
  recentBlocks: Array<{
    id: number;
    identifier: string;
    type: "IP" | "EMAIL";
    action: string;
    count: number;
    isBlocked: boolean;
    blockedUntil: string | null;
    reason: string | null;
    lastAttempt: string;
  }>;
}

const AbuseStatsPage: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [stats, setStats] = useState<AbuseStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAbuseStats();
  }, []);

  const fetchAbuseStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await httpService.getAuth<AbuseStats>("/admin/abuse-stats");
      setStats(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch abuse stats");
    } finally {
      setLoading(false);
    }
  };

  const unblockIdentifier = async (identifier: string, type: "IP" | "EMAIL", action: string) => {
    try {
      await httpService.postAuth("/admin/unblock", {
        identifier,
        type,
        action,
      });
      // Refresh stats after unblocking
      await fetchAbuseStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to unblock identifier");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getActionBadge = (action: string) => {
    const colors = {
      signup: "bg-blue-100 text-blue-800",
      otp_request: "bg-yellow-100 text-yellow-800",
      otp_verify: "bg-red-100 text-red-800",
    };
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${
          colors[action as keyof typeof colors] || "bg-gray-100 text-gray-800"
        }`}
      >
        {action.replace("_", " ")}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className={`p-4 rounded-lg ${
            isDarkMode ? "bg-red-900/20 border border-red-800" : "bg-red-50 border border-red-200"
          }`}
        >
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <p className={`text-sm ${isDarkMode ? "text-red-400" : "text-red-600"}`}>{error}</p>
          </div>
          <button
            onClick={fetchAbuseStats}
            className={`mt-4 px-4 py-2 rounded transition-colors ${
              isDarkMode ? "bg-blue-600 text-rp-grey hover:bg-blue-700" : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className={`p-4 rounded-lg ${isDarkMode ? "bg-rp-surface" : "bg-gray-50"}`}>
        <p className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>No abuse statistics available</p>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="Abuse Statistics"
      description="Monitor and manage abuse attempts and blocks"
      actions={
        <button
          onClick={fetchAbuseStats}
          className={`px-4 py-2 text-sm font-medium rounded-lg border ${
            isDarkMode
              ? "bg-rp-surface border-rp-separator text-rp-grey/70 hover:bg-rp-surface-raised"
              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          <RefreshCw className="w-4 h-4 mr-2 inline" />
          Refresh
        </button>
      }
    >
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            className={`p-6 rounded-lg border ${
              isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"
            }`}
          >
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
                  Total Blocked
                </p>
                <p className={`text-2xl font-bold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                  {stats.totalBlocked}
                </p>
              </div>
            </div>
          </div>

          <div
            className={`p-6 rounded-lg border ${
              isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"
            }`}
          >
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
                  Top Blocked IPs
                </p>
                <p className={`text-2xl font-bold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                  {stats.topBlockedIPs.length}
                </p>
              </div>
            </div>
          </div>

          <div
            className={`p-6 rounded-lg border ${
              isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"
            }`}
          >
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className={`text-sm font-medium ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
                  Top Blocked Emails
                </p>
                <p className={`text-2xl font-bold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                  {stats.topBlockedEmails.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Top Blocked IPs */}
        {stats.topBlockedIPs.length > 0 && (
          <div
            className={`rounded-lg border ${isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"}`}
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className={`text-lg font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                Top Blocked IP Addresses
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {stats.topBlockedIPs.map((item, index) => (
                <div key={index} className="px-6 py-4 flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>{item.ip}</p>
                    <p className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
                      {item.count} attempts
                    </p>
                  </div>
                  <button
                    onClick={() => unblockIdentifier(item.ip, "IP", "signup")}
                    className="px-3 py-1 text-sm text-green-600 hover:text-green-800 font-medium"
                    title="Unblock this IP address to allow signup attempts again"
                  >
                    <Unlock className="w-4 h-4 mr-1 inline" />
                    Unblock
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Blocked Emails */}
        {stats.topBlockedEmails.length > 0 && (
          <div
            className={`rounded-lg border ${isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"}`}
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className={`text-lg font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                Top Blocked Email Addresses
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {stats.topBlockedEmails.map((item, index) => (
                <div key={index} className="px-6 py-4 flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>{item.email}</p>
                    <p className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
                      {item.count} attempts
                    </p>
                  </div>
                  <button
                    onClick={() => unblockIdentifier(item.email, "EMAIL", "signup")}
                    className="px-3 py-1 text-sm text-green-600 hover:text-green-800 font-medium"
                    title="Unblock this email address to allow signup attempts again"
                  >
                    <Unlock className="w-4 h-4 mr-1 inline" />
                    Unblock
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Blocks */}
        {stats.recentBlocks.length > 0 && (
          <div
            className={`rounded-lg border ${isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"}`}
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className={`text-lg font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>Recent Blocks</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`${isDarkMode ? "bg-rp-surface-sunken" : "bg-gray-50"}`}>
                  <tr>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                      }`}
                    >
                      Identifier
                    </th>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                      }`}
                    >
                      Type
                    </th>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                      }`}
                    >
                      Action
                    </th>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                      }`}
                    >
                      Count
                    </th>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                      }`}
                    >
                      Last Attempt
                    </th>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                      }`}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
                  {stats.recentBlocks.map(block => (
                    <tr key={block.id} className={`${isDarkMode ? "bg-rp-surface text-rp-grey/70" : "bg-white text-gray-900"}`}>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                          isDarkMode ? "text-rp-grey" : "text-gray-900"
                        }`}
                      >
                        {block.identifier}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                        }`}
                      >
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            block.type === "IP" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"
                          }`}
                        >
                          {block.type}
                        </span>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                        }`}
                      >
                        {getActionBadge(block.action)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                        }`}
                      >
                        {block.count}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                        }`}
                      >
                        {formatDate(block.lastAttempt)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                        }`}
                      >
                        <button
                          onClick={() => unblockIdentifier(block.identifier, block.type, block.action)}
                          className="text-green-600 hover:text-green-800 font-medium"
                          title={`Unblock this ${block.type.toLowerCase()} to allow ${block.action.replace(
                            "_",
                            " "
                          )} attempts again`}
                        >
                          <Unlock className="w-4 h-4 mr-1 inline" />
                          Unblock
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {stats.totalBlocked === 0 && (
          <div className={`text-center py-12 ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No abuse detected</p>
            <p className="text-sm">The system is running smoothly with no blocked identifiers.</p>
          </div>
        )}
      </div>
    </AdminPageLayout>
  );
};

export default AbuseStatsPage;
